import { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { isOwnStylist } from '../utils/dashboardFilters';

// Mirrors app/services/access_policy.py — the backend is the boundary, this only shapes the UI.
// may open the store/stylist picker. Never `stylist`: a stylist sees only their own record (owner MUST rule 2026-09-29).
const ADMIN_ROLES = new Set(['manager', 'beauty_manager', 'company_admin', 'dev']);
const IAM_ROLES = new Set(['manager', 'beauty_manager', 'company_admin', 'dev']); // may open IAM (invite + own invitees) = 管理画面
const APPROVER_ROLES = new Set(['manager', 'beauty_manager', 'company_admin', 'dev']); // may 確定 行動 (goals_service.APPROVER_ROLES)

/**
 * Firebase custom claim `role` — same pattern as SettingsPage.jsx.
 */
export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(Boolean(user));

  useEffect(() => {
    if (!user) {
      setRole(null);
      setCompanyId(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    user.getIdTokenResult().then((res) => {
      if (!cancelled) {
        setRole(res.claims?.role || null);
        // Set by invite_service alongside `role`; absent for dev and for accounts invited before it existed.
        setCompanyId(res.claims?.company_id || null);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setRole(null);
        setCompanyId(null);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [user]);

  return {
    role,
    companyId,
    loading,
    isAdmin: role != null && ADMIN_ROLES.has(role),
    isDev: role === 'dev',
    canAccessIam: role != null && IAM_ROLES.has(role),
    canApprove: role != null && APPROVER_ROLES.has(role),
    // 目標 is 確定ed by managers only; 経営者 sees the row and history (owner 2026-10-22)
    canApproveGoals: role != null && APPROVER_ROLES.has(role) && role !== 'company_admin',
    isStylist: role === 'stylist',
  };
}

/**
 * May the signed-in user edit goals / actions / check-ins for `stylist`?
 *
 * A manager is a stylist too: they edit their own record and read the rest of
 * the team. Ownership is decided by Firebase displayName, the same link the
 * stylist auto-select uses (DashboardContext). Mirrors
 * can_write_stylist_goals() in app/services/goals_service.py — the backend is
 * the real boundary; this only keeps the UI from offering controls that 403.
 */
export function useCanEditStylist(stylist) {
  const { user } = useAuth();
  const { role } = useUserRole();
  if (role === 'dev') return true;
  return isOwnStylist(user?.displayName, stylist);
}

/** @deprecated use getSettingsPath from utils/dashboardNav (preserves filters). */
export { getSettingsPath } from '../utils/dashboardNav';
